import { useApi } from '@hooks/useApi';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { USER_COLUMNS } from '@protos/user';
import { Dashboard } from '@protos/v2/dashboard';
import { toastifyService } from '@services/ToastifyService';
import { BlotterWidget } from '@shared/components/BlotterWidget';
import theme from '@shared/themes/darkTheme';
import { ColDef } from 'ag-grid-enterprise';
import { useCallback, useState } from 'react';

interface ShareDashboardDialogProps {
  dashboard: Dashboard | null;
  isOpen: boolean;
  onClose: () => void;
}

const dialogColumns = ['account_name', 'full_name', 'email'];
const getRowId = ({ data }): string => data?.id?.toString();

const defaultColDef = (chosenUsers: string[], setChosenUsers: (userIds: string[]) => void) => {
  return {
    sortable: true,
    filter: false,
    floatingFilter: false,
    resizable: true,
    onCellClicked(event) {
      if (chosenUsers.includes(event.data.id)) {
        setChosenUsers(chosenUsers.filter(user => user !== event.data.id));
      } else {
        setChosenUsers([...chosenUsers, event.data.id]);
      }
    },
    cellStyle(params) {
      if (chosenUsers.includes(params.data.id)) {
        return { cursor: 'pointer', backgroundColor: theme.palette.info.main };
      }
      return { cursor: 'pointer' };
    },
  } as ColDef;
};

export const ShareDashboardDialog = ({ dashboard, isOpen, onClose }: ShareDashboardDialogProps) => {
  const { apiClient } = useApi();

  const [chosenUsers, setChosenUsers] = useState<string[]>([]);
  const [gridHeight, ignored] = useState(() => {
    return document?.documentElement?.clientHeight - 500;
  });

  const dataLoader = apiClient?.dashboardUsersLoader();

  const onDashboardShare = useCallback(async () => {
    if (apiClient && dashboard) {
      try {
        await Promise.all(chosenUsers.map(user => apiClient.shareDashboard(dashboard.id, user)));
        toastifyService.showSuccess(`${chosenUsers.length} dashboard${chosenUsers.length > 1 ? 's' : ''} shared successfully.`);
      } catch (e: any) {
        toastifyService.showError(`Error on sharing dashboard. ${e?.data?.detail}`);
      }
    }
    onClose();
  }, [apiClient, dashboard, chosenUsers]);

  if (!dataLoader) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      disableEnforceFocus={false}
      disableAutoFocus={false}
      fullWidth
      maxWidth="md"
      PaperProps={{
        component: 'form',
        onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          onDashboardShare();
        },
      }}
    >
      <DialogTitle>Choose user to share dashboard with</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flex: 1 }}>
          <BlotterWidget
            maxHeight={gridHeight}
            dataLoader={dataLoader}
            columnDefs={USER_COLUMNS.filter(col => dialogColumns.includes(col.field!))}
            getRowId={getRowId}
            defaultColDef={defaultColDef(chosenUsers, setChosenUsers)}
            enableCellTextSelection
            filters={[{} as any]} // add empty filter, so blotter can show search bar
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} size="small" style={{ fontSize: 11 }}>
          Cancel
        </Button>
        <Button disabled={!chosenUsers.length} type="submit" variant="contained" size="small" style={{ fontSize: 11 }}>
          Share
        </Button>
      </DialogActions>
    </Dialog>
  );
};
